import { Flex, Text } from "@mantine/core";
import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { S3_URI_PREFIX } from "@/constants/s3";
import type { Nullable } from "@/types/utils";
import { FileDownloadLink } from "./file-download-link";
import {
  VOICE_TRANSCRIPTION_DISCLAIMER,
  VoiceAnswerLink,
} from "./voice-answer-link";
import { VoiceAnswerLinkLazy } from "./voice-answer-link-lazy";
import { ResultsSection } from "./results-section";
import { MessageSenders } from "@/types/screeners";

interface TranscriptBlock {
  audioPlaybackUrl?: string;
  internalLabel?: string;
  isFileResponse?: boolean;
  isVoiceResponse?: boolean;
  message: string;
  name: MessageSenders;
  questionRootId?: string;
  rightSection?: ReactNode;
  transcriptionText?: string;
}

interface FileContext {
  candidateName: Nullable<string>;
  createdAt: string;
  jobTitle: string;
}

interface QuestionTranscriptProps {
  fileContext?: FileContext;
  sessionId: string;
  transcript: TranscriptBlock[];
  title: string;
}

export const QuestionTranscript = ({
  fileContext,
  sessionId,
  title,
  transcript,
}: QuestionTranscriptProps) => {
  return (
    <ResultsSection
      style={{ paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }}
      title={<Text className="font-semibold px-5">{title}</Text>}
    >
      <Flex direction="column">
        {transcript.map(
          (
            {
              audioPlaybackUrl,
              internalLabel,
              isFileResponse,
              isVoiceResponse,
              message,
              name,
              questionRootId,
              rightSection,
              transcriptionText,
            },
            index,
          ) => {
            return (
              <Flex
                className={twMerge([
                  name === MessageSenders.Screener && "bg-uw-gray-0",
                  "px-5",
                  "py-3",
                ])}
                direction="column"
                key={`${name}_${index}`}
              >
                <Flex align="center" justify="space-between">
                  <Text
                    className="font-bold text-uw-gray-7 uppercase"
                    size="xs"
                  >
                    {name}
                  </Text>
                  {rightSection}
                </Flex>
                {isFileResponse &&
                questionRootId &&
                fileContext &&
                internalLabel &&
                message.startsWith(S3_URI_PREFIX) ? (
                  <FileDownloadLink
                    candidateName={fileContext.candidateName}
                    createdAt={fileContext.createdAt}
                    internalLabel={internalLabel}
                    jobTitle={fileContext.jobTitle}
                    questionRootId={questionRootId}
                    s3Uri={message}
                    sessionId={sessionId}
                  />
                ) : isVoiceResponse ? (
                  <Flex direction="column" gap="xs">
                    {audioPlaybackUrl ? (
                      <VoiceAnswerLink
                        href={audioPlaybackUrl}
                        sessionId={sessionId}
                      />
                    ) : message.startsWith(S3_URI_PREFIX) ? (
                      // AS-1830: AI Screener 3.0 sessions don't pre-compute
                      // an audioPlaybackUrl per voice question — the
                      // recording is referenced only by its `s3://` key in
                      // the candidate message content. Fall back to the
                      // lazy variant which exchanges the key for a
                      // presigned URL on click.
                      <VoiceAnswerLinkLazy
                        audioKey={message}
                        sessionId={sessionId}
                      />
                    ) : null}
                    {transcriptionText && (
                      <>
                        <Text c="gray.7" className="italic" size="xs">
                          {transcriptionText}
                        </Text>
                        <Text c="gray.5" size="xs">
                          {VOICE_TRANSCRIPTION_DISCLAIMER}
                        </Text>
                      </>
                    )}
                    {!transcriptionText && audioPlaybackUrl && (
                      <Text c="gray.5" className="italic" size="xs">
                        Transcription pending...
                      </Text>
                    )}
                  </Flex>
                ) : (
                  <Text className="text-uw-gray-9" size="sm">
                    {message}
                  </Text>
                )}
              </Flex>
            );
          },
        )}
      </Flex>
    </ResultsSection>
  );
};
